import * as React from "react";
import type { CollaborateSelectionContext } from "../types/selection";
import type { ComposerSnippet, CollaborateAgreementVersion } from "../types/collaborateThread";
import {
  effectiveDocumentQuote,
  hasDocumentSelectionQuote,
} from "../utils/documentSelectionQuote";
import { createThread } from "../api";
import { captureSelectionAnchor, isAnchorSupported } from "../utils/collaborateAnchor";
import { useCollaborateUserList } from "../hooks/useCollaborateSelectors";
import { useCollaborateStore } from "../store/useCollaborateStore";
import { hasPlaybookTag, parsePlaybookAssigneeFromEditor } from "../utils/parsePlaybook";
import CollaborateCommentEditor, {
  type CollaborateCommentEditorHandle,
} from "./editor/CollaborateCommentEditor";
import { showCollabToast } from "./ui/CollabToast";
import { showPluginToast } from "../../taskpane/components/shell/PluginToast";
import CollabPostButton from "./ui/CollabPostButton";
import { IconX } from "./icons";

const COMPOSE_PLACEHOLDER =
  "Type / for commands, @ to mention. Press Shift + Enter for a new line.";

/** Select value for Tag to → NA (no version pill). */
const TAG_NA = "__na__";

export type ComposerProps = {
  snippet: ComposerSnippet | null;
  /** Live Word selection while the composer is open. */
  documentSelection?: CollaborateSelectionContext | null;
  autoFocus?: boolean;
  /** Fired after a successful post that included quoted document selection. */
  onSelectionConsumed?: () => void;
  versions?: CollaborateAgreementVersion[];
  defaultVersionId?: string | null;
};

function truncateQuote(quote: string, max = 140): string {
  if (!quote) {
    return "";
  }
  return quote.length > max ? `${quote.slice(0, max)}…` : quote;
}

const Composer: React.FC<ComposerProps> = ({
  snippet,
  documentSelection = null,
  autoFocus = true,
  onSelectionConsumed,
  versions = [],
  defaultVersionId = null,
}) => {
  const editorRef = React.useRef<CollaborateCommentEditorHandle | null>(null);
  const [submitting, setSubmitting] = React.useState(false);
  const [quoteDismissed, setQuoteDismissed] = React.useState(false);
  const [canPost, setCanPost] = React.useState(false);
  const [boldActive, setBoldActive] = React.useState(false);
  const [italicActive, setItalicActive] = React.useState(false);
  const [selectedVersionId, setSelectedVersionId] = React.useState(() => {
    const preferred = defaultVersionId?.trim();
    if (preferred && versions.some((version) => version.id === preferred)) {
      return preferred;
    }
    return versions[0]?.id ?? "";
  });
  const userList = useCollaborateUserList();

  const sortedVersions = React.useMemo(
    () => [...versions].sort((a, b) => b.version - a.version),
    [versions],
  );

  React.useEffect(() => {
    if (versions.length === 0) {
      return;
    }
    const preferred = defaultVersionId?.trim();
    if (preferred && versions.some((version) => version.id === preferred)) {
      setSelectedVersionId((current) => current || preferred);
      return;
    }
    setSelectedVersionId((current) => current || versions[0]?.id || "");
  }, [defaultVersionId, versions]);

  const selectionQuoteKey = `${snippet?.quote ?? ""}\0${documentSelection?.quote ?? ""}`;
  React.useEffect(() => {
    setQuoteDismissed(false);
  }, [selectionQuoteKey]);

  React.useEffect(() => {
    if (!autoFocus) {
      return undefined;
    }
    const t = window.setTimeout(() => editorRef.current?.focus(), 50);
    return () => window.clearTimeout(t);
  }, [autoFocus, snippet]);

  const syncToolbar = React.useCallback(() => {
    const editor = editorRef.current?.getEditor() ?? null;
    setBoldActive(Boolean(editor?.isActive("bold")));
    setItalicActive(Boolean(editor?.isActive("italic")));
  }, []);

  React.useEffect(() => {
    const interval = window.setInterval(syncToolbar, 200);
    return () => window.clearInterval(interval);
  }, [syncToolbar]);

  const toggleMark = (mark: "bold" | "italic") => {
    const editor = editorRef.current?.getEditor();
    if (!editor || submitting) {
      return;
    }
    if (mark === "bold") {
      editor.chain().focus().toggleBold().run();
    } else {
      editor.chain().focus().toggleItalic().run();
    }
    syncToolbar();
  };

  const onPost = async () => {
    const body = editorRef.current?.getSerializedBody().trim() ?? "";
    if (!body) {
      showCollabToast.warning("Write a comment before posting.");
      return;
    }

    if (editorRef.current?.isSignoffPending()) {
      showCollabToast.warning("Select someone for sign-off: type @ after the Sign-off badge.");
      return;
    }

    if (editorRef.current?.isPlaybookPending()) {
      showCollabToast.warning(
        "Select someone for playbook review: type @ after the Update playbook badge.",
      );
      return;
    }

    const assignee = editorRef.current?.getSignoffAssignee(userList) ?? null;
    const editor = editorRef.current?.getEditor() ?? null;
    const playbookAssignee =
      editor && hasPlaybookTag(body) ? parsePlaybookAssigneeFromEditor(editor, userList) : null;
    if (assignee && playbookAssignee) {
      showCollabToast.warning("Use either Sign-off or Update playbook in one comment, not both.");
      return;
    }
    if (hasPlaybookTag(body) && !playbookAssignee) {
      showCollabToast.warning(
        "Could not resolve the playbook assignee. Type @ after the Update playbook badge.",
      );
      return;
    }

    const selectionQuote = quoteDismissed
      ? ""
      : effectiveDocumentQuote(snippet, documentSelection);
    if (hasPlaybookTag(body) && !selectionQuote) {
      showCollabToast.warning("Select text in the document first to update the playbook.");
      return;
    }

    const requireApproval = Boolean(assignee);
    const mentionIds = editorRef.current?.getMentionIds(userList) ?? [];
    const hadSelectionQuote = Boolean(selectionQuote);
    const versionTagged = selectedVersionId !== TAG_NA && Boolean(selectedVersionId.trim());
    const resolvedVersionId =
      (versionTagged ? selectedVersionId : "") || defaultVersionId || sortedVersions[0]?.id || "";
    const anchorVersionId = resolvedVersionId;

    setSubmitting(true);
    try {
      const anchor =
        hadSelectionQuote && isAnchorSupported() && anchorVersionId
          ? await captureSelectionAnchor(anchorVersionId, selectionQuote)
          : null;

      await createThread({
        body,
        mentions: mentionIds,
        requireApproval,
        assignee,
        playbookAssignee,
        snippet: { quote: selectionQuote, anchor },
        anchor,
        agreementVersionId: resolvedVersionId || undefined,
        versionTagged,
      });
      if (hadSelectionQuote) {
        onSelectionConsumed?.();
      }
      editorRef.current?.clear();
      setCanPost(false);
      showPluginToast("Comment posted.");
    } catch (err) {
      const msg = err instanceof Error ? err.message : String(err);
      showCollabToast.error(msg || "Could not post the comment.");
    } finally {
      setSubmitting(false);
    }
  };

  const hasQuote =
    !quoteDismissed && hasDocumentSelectionQuote(snippet, documentSelection);
  const displayQuote = quoteDismissed
    ? ""
    : effectiveDocumentQuote(snippet, documentSelection);

  const clearSelectedQuote = () => {
    setQuoteDismissed(true);
    useCollaborateStore.getState().openComposer({ quote: "" });
    onSelectionConsumed?.();
  };

  const selectedVersionLabel =
    sortedVersions.find((v) => v.id === selectedVersionId)?.label ??
    (sortedVersions.find((v) => v.id === selectedVersionId)
      ? `v${sortedVersions.find((v) => v.id === selectedVersionId)!.version}`
      : "v1");

  return (
    <div className="collaborate-composer">
      {hasQuote ? (
        <div className="collab-quoted collab-quoted--composer">
          <div className="collab-quoted__header">
            <div className="collab-quoted__overline">Selected text</div>
            <button
              type="button"
              className="collab-quoted__dismiss"
              aria-label="Remove selected text"
              onClick={clearSelectedQuote}
              disabled={submitting}
            >
              <IconX size={12} />
            </button>
          </div>
          <div className="collab-quoted__text">&ldquo;{truncateQuote(displayQuote)}&rdquo;</div>
        </div>
      ) : null}
      <CollaborateCommentEditor
        ref={editorRef}
        users={userList}
        compact
        enableSlashCommands
        hasDocumentSelection={() =>
          !quoteDismissed && hasDocumentSelectionQuote(snippet, documentSelection)
        }
        disabled={submitting}
        placeholder={COMPOSE_PLACEHOLDER}
        onChange={({ isEmpty }) => setCanPost(!isEmpty)}
        onSubmit={() => {
          if (!submitting && canPost) void onPost();
        }}
      />
      <div className="collab-compose-toolbar">
        <div className="collab-compose-toolbar__marks" role="toolbar" aria-label="Formatting">
          <button
            type="button"
            className={`collab-micro-btn${boldActive ? " is-active" : ""}`}
            title="Bold"
            aria-label="Bold"
            aria-pressed={boldActive}
            disabled={submitting}
            onMouseDown={(e) => {
              e.preventDefault();
              toggleMark("bold");
            }}
          >
            B
          </button>
          <button
            type="button"
            className={`collab-micro-btn${italicActive ? " is-active" : ""}`}
            title="Italic"
            aria-label="Italic"
            aria-pressed={italicActive}
            disabled={submitting}
            onMouseDown={(e) => {
              e.preventDefault();
              toggleMark("italic");
            }}
          >
            <em>I</em>
          </button>
        </div>
        <div className="collab-compose-toolbar__end">
          {sortedVersions.length > 0 ? (
            <label className="collab-tag-to">
              <span>Tag to</span>
              <select
                value={selectedVersionId}
                onChange={(e) => setSelectedVersionId(e.target.value)}
                disabled={submitting}
                aria-label="Tag comment to agreement version"
              >
                <option value={TAG_NA}>NA</option>
                {sortedVersions.map((version) => (
                  <option key={version.id} value={version.id}>
                    {version.label ?? `v${version.version}`}
                  </option>
                ))}
              </select>
            </label>
          ) : (
            <span className="collab-tag-to-label">Tag to {selectedVersionLabel}</span>
          )}
          <CollabPostButton
            onClick={() => void onPost()}
            disabled={submitting || !canPost}
            loading={submitting}
            ready={canPost}
          />
        </div>
      </div>
    </div>
  );
};

export default Composer;
